import { useEffect, type ReactNode } from 'react';
import {
  Box,
  Card,
  Divider,
  Stack,
  Text,
  Alert,
  Button,
  ResultListItem,
  Skeleton,
  LocationIcon,
  ChevronRightIcon,
} from '@citibox-panel/ui';
import type { LocationMatch } from '@citibox-panel/api-client';
import { useLocationSearch } from '../hooks/useLocationSearch';

export interface CoincidencesStepProps {
  query: string;
  onPick: (match: LocationMatch) => void;
  onResolved: (term: string, matches: number) => void;
  searchLocations: (q: string) => Promise<LocationMatch[]>;
}

export function CoincidencesStep({
  query,
  onPick,
  onResolved,
  searchLocations,
}: CoincidencesStepProps) {
  const { data, resolvedQuery, isTooShort, isSearching, isError, refetch } = useLocationSearch(
    query,
    searchLocations,
  );

  // Reports rather than emits, because the dedupe guard has to outlive a step that dies on every
  // transition. Keyed on the RESOLVED term: the typed one reports the previous term's count.
  useEffect(() => {
    if (resolvedQuery === undefined || data === undefined || isError) return;
    onResolved(resolvedQuery, data.length);
  }, [resolvedQuery, data, isError, onResolved]);

  // Whichever line applies, so one region carries every outcome. The 0-match line names the term
  // the zero belongs to: bound to `query` it would announce once per keystroke, naming a term the
  // zero does not belong to, since inside the debounce the query key has not moved.
  // TODO: i18n
  const outcomeLine = (): ReactNode => {
    // The floor answers the TYPED term, so the hint lands on the keystroke that goes below it.
    if (isTooShort)
      return (
        <Text variant="body2" color="secondary">
          Escribe al menos 3 caracteres.
        </Text>
      );
    if (data === undefined || resolvedQuery === undefined) return null;
    return data.length > 0 ? (
      // Set in the rows' own weight: this labels the card beneath it, and a caption in the body
      // weight reads as a sentence about the list rather than as its heading.
      <Text variant="overline" color="secondary" bold dense>
        {`${String(data.length)} ${data.length === 1 ? 'COINCIDENCIA' : 'COINCIDENCIAS'}`}
      </Text>
    ) : (
      <Text variant="body2" color="secondary">{`Sin resultados para "${resolvedQuery}"`}</Text>
    );
  };

  return (
    <Stack spacing={2}>
      {/* Mounted in every state, so the region exists BEFORE its content changes — a region that
          arrives with its text is the update assistive tech routinely misses. */}
      <Box role="status">{outcomeLine()}</Box>
      {isError && (
        <Box>
          {/* Outside the region, which stays empty here: an alert carries its own semantics and
              must not be paraphrased by a status region beside it. */}
          {/* TODO: i18n */}
          <Alert severity="error">No se pudo completar la búsqueda.</Alert>
          <Box sx={{ mt: 1 }}>
            {/* TODO: i18n */}
            <Button variant="text" onClick={refetch}>
              Reintentar
            </Button>
          </Box>
        </Box>
      )}
      {isSearching && (
        <Stack spacing={1}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={32} />
          ))}
        </Stack>
      )}
      {data !== undefined && data.length > 0 && (
        // ONE outlined surface around the whole list, with the rows ruled off from one another
        // inside it. A border per row would read as a stack of cards to choose between rather than
        // as a list to scan — and the card only exists while there are rows to frame, so an empty
        // result stays a sentence instead of an outline drawn around one.
        <Card variant="outlined">
          {data.map((match, i) => (
            // A building has one row per portal, so `id` alone repeats. The address is what
            // distinguishes two rows of the same building.
            <Box key={`${String(match.id)}-${match.address}`}>
              {/* Before each row but the first, so the rule always sits BETWEEN two rows and the
                  card's own border is the only edge above the list and below it. */}
              {i > 0 && <Divider />}
              <ResultListItem
                icon={<LocationIcon />}
                label={match.address}
                trailing={<ChevronRightIcon />}
                onClick={() => {
                  onPick(match);
                }}
              />
            </Box>
          ))}
        </Card>
      )}
    </Stack>
  );
}
